import userService from "api/user/user.service";
import { trackConversion, trackEvent, trackFacebookEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

// The five self-serve plan codes the GA4 purchase funnel knows. Hoisted so the mapping is one
// place instead of an if/else-if chain — and so an unknown code takes the explicit OTHER branch
// rather than producing no event at all.
export const PURCHASE_EVENT_BY_PACKAGE_TYPE: Record<string, string> = {
  design_starter: EVENTS.PRICING.PURCHASE_DESIGN_STARTER,
  photoshoot_starter: EVENTS.PRICING.PURCHASE_PHOTOSHOOT_STARTER,
  design_pro: EVENTS.PRICING.PURCHASE_DESIGN_PRO,
  design_studio: EVENTS.PRICING.PURCHASE_DESIGN_STUDIO,
  creative_suite: EVENTS.PRICING.PURCHASE_CREATIVE_SUITE,
};

/* ------------------------------------------------------------------------------------------
 * Pending purchase attribution
 *
 * Verified against refabric-pro-api origin/master (ecc2c6661, 2026-09-23):
 *
 * - The label used to come from `user_info.package_type`, which is `teams.package_type`
 *   (models/users.py). The active billing flow NEVER updates that column for a team that already
 *   exists — `_ensure_team_for_user` is a no-op for an existing team, and `invoice.paid`
 *   provisioning writes subscriptions, seats, credits and `teams.permissions` only
 *   (modules/billing/handlers.py, services/packages_service.py). So for a returning customer it is
 *   their OLD plan (or the string "false" once expired) indefinitely — not merely until a webhook
 *   lands. Re-reading cannot fix it. That is how a 2026-09-15 Design Pro payment landed in GA4 as
 *   purchase_plan_design_starter and a 2026-09-17 Design Starter payment as design_pro.
 * - `package_detail[]` in the same response IS built from the team's active subscriptions
 *   (services/users.py → subscription_repo.get_by_user: status active|trialing|past_due), and each
 *   row carries the subscription's `price_id`, the plan `code` (packages.package_code) and a
 *   seat-multiplied `total_price`. ⚠️ Its `package_type` is the package GROUP, not the plan code.
 *
 * So: remember the Stripe price id when the browser is handed to a CHECKOUT, and on the success
 * page wait for the subscription row with that price id. Its `code` is the plan that was bought
 * and its `total_price` (catalog price × seats) is the reported value. No BE change, no extra
 * endpoint. ⚠️ That value is an ESTIMATE, not revenue: it ignores promotion codes, tax and
 * proration, and the BE has no payment-specific total to offer on this path.
 *
 * Only `action: "checkout"` records — per modules/billing/handlers.py a checkout returns to
 * `/payment/success/:stateId`, an in-portal `upgrade` to `/pricing?upgrade_state=`, and a seat
 * change (POST /payments/seats) to `/account`; neither of the latter reaches the success page.
 *
 * ⚠️ Known limits (all pre-existing, none made worse here):
 *  - Free trials. A trial checkout lands on the same success page. Its subscription is provisioned
 *    as `{sub}_trial` at the trial CAMPAIGN's price id, not the checkout's (handlers.py
 *    `is_trial_invoice`; also any zero-total first invoice, e.g. a 100 % promo), so the price match
 *    misses and the trial is reported under the legacy label exactly as before. `package_detail`
 *    does not expose subscription status, so the FE cannot suppress it.
 *  - A matching row can pre-exist: team scope includes teammates' and synthetic (`sub_fake_*`)
 *    subscriptions. Checkout normally refuses a plan the team already holds, so this is an edge;
 *    the match then proves a team subscription at that price, not this payment.
 *
 * ⚠️ ANALYTICS ATTRIBUTION ONLY. Client-written and forgeable — never read it for authorization,
 * entitlements, credits, billing or any other product behaviour.
 * ---------------------------------------------------------------------------------------- */

const PENDING_PURCHASE_KEY = "pending_purchase_package";

// An abandoned checkout leaves the value behind. Every later checkout redirect overwrites or
// clears it, so the residual exposure is small — bounded to an hour, far longer than a Stripe
// checkout takes and far shorter than a work session.
export const PENDING_PURCHASE_TTL_MS = 60 * 60 * 1000;

export interface PendingPurchase {
  /** The Stripe price id of the checkout — every entry point holds it. */
  priceId: string;
  /** Doubles as the attempt token: a consumer clears only the value it actually read. */
  createdAt: number;
}

const readPendingRecord = (): PendingPurchase | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;

    const pending = JSON.parse(raw);
    if (typeof pending?.priceId !== "string" || !pending.priceId || typeof pending?.createdAt !== "number") return null;

    return { priceId: pending.priceId, createdAt: pending.createdAt };
  } catch {
    return null;
  }
};

/**
 * Clear the pending value. With `createdAt`, clear it only if it is still the value that was
 * read — a slow success-page report must not erase a newer checkout started in the meantime.
 */
export const clearPendingPurchasePackage = (createdAt?: number) => {
  try {
    if (createdAt != null && readPendingRecord()?.createdAt !== createdAt) return;

    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    // no-op
  }
};

export interface CheckoutAttribution {
  /** The Stripe price id of the plan being bought. */
  priceId?: string;
}

export const setPendingPurchase = ({ priceId }: CheckoutAttribution, now: number = Date.now()) => {
  // Nothing to record: CLEAR rather than no-op. Leaving the previous value behind would let an
  // abandoned checkout's price attribute a later, different purchase in the same tab — no
  // attribution is always better than someone else's.
  if (!priceId) return clearPendingPurchasePackage();

  try {
    const pending: PendingPurchase = { priceId, createdAt: now };

    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(pending));
  } catch {
    // Storage disabled or full — attribution degrades to the legacy read, nothing breaks.
  }
};

/** The pending record, or null when absent, expired or malformed. */
export const readPendingPurchase = (now: number = Date.now()): PendingPurchase | null => {
  const pending = readPendingRecord();
  if (!pending) return null;

  // `now < createdAt` catches a clock moved backwards; treat it as unusable rather than fresh.
  if (now - pending.createdAt > PENDING_PURCHASE_TTL_MS || now < pending.createdAt) return null;

  return pending;
};

/**
 * Record the attribution for a payment action that is about to leave the app.
 *
 * Called by `usePaymentActionHandler` for every caller (Pricing, Account, Login, SSO, SignUp) and
 * by CheckoutPage, which redirects itself — so no checkout entry point can skip it. A caller with
 * no price id CLEARS any stale value, so an abandoned checkout can never label a different one.
 */
export const recordCheckoutAttribution = (result: { redirected: boolean; item?: { action?: string } | null }, attribution: CheckoutAttribution = {}) => {
  if (!result.redirected || result.item?.action !== "checkout") return;

  setPendingPurchase(attribution);
};

/* ------------------------------------------------------------------------------------------
 * Backend read
 * ---------------------------------------------------------------------------------------- */

// The subscription row is written by the Stripe webhook, which can land a few seconds after the
// browser is back on the success page — re-read until it (or, with no pending price, a mapped
// read-back code) shows up. 4 reads, 2.5 s apart: a 7.5 s window.
const PURCHASE_READ_ATTEMPTS = 4;
const PURCHASE_READ_RETRY_DELAY_MS = 2500;

export interface PurchasedPackage {
  /** Legacy `user_info.package_type` (= teams.package_type) — see the banner; often stale. */
  packageType: string;
  packageDetail: any[];
}

/**
 * Only a Stripe-billed row can be a purchase (STRIPE Phase 3, reviewer 95). v3 marks backoffice demo / agreement / comp rows
 * `billing_source: "manual"` — they are stored as package_type "paid" too, so neither `billing_type` nor a price tells them
 * apart. STRIPE P5 / PD10: v3 sends the field on every row, so a row without it is not proof of a Stripe purchase either.
 */
const isStripeBilled = (row: any): boolean => row?.billing_source === "stripe";

/** The active-subscription row for the checkout's price, if the webhook has written it. */
export const findPurchasedRow = (packageDetail: any[], priceId: string | undefined) => (priceId ? packageDetail.find((row) => row?.price_id === priceId) : undefined);

export const readPurchasedPackage = async (userId: string, { priceId }: { priceId?: string } = {}): Promise<PurchasedPackage | null> => {
  let latest: PurchasedPackage | null = null;

  for (let attempt = 0; attempt < PURCHASE_READ_ATTEMPTS; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, PURCHASE_READ_RETRY_DELAY_MS));

    // [HTTP_HANDLED_RESPONSE] 401/403/409/503 RESOLVE with responseData: null + _handled: true,
    // so `catch` never fires — check `_handled` first. A body without `user_info` is not a
    // profile read either.
    const res: any = await userService.getUserDetail({ user_id: userId });
    const userInfo = res?._handled ? null : res?.responseData?.user_info;

    // A failure on a LATER attempt keeps the read we already have — that one did succeed.
    if (!userInfo || typeof userInfo !== "object") return latest;

    latest = { packageType: userInfo.package_type?.toLowerCase() ?? "", packageDetail: (res.responseData.package_detail || []).filter(isStripeBilled) };

    if (priceId ? findPurchasedRow(latest.packageDetail, priceId) : PURCHASE_EVENT_BY_PACKAGE_TYPE[latest.packageType]) return latest;
  }

  return latest;
};

/* ------------------------------------------------------------------------------------------
 * Emission
 * ---------------------------------------------------------------------------------------- */

// Google Ads purchase conversion, sent as `send_to: AW-11423224913/...` through the global
// `window.gtag`. Note the success document does NOT itself run `gtag("config", "AW-11423224913")`
// — only Login/SignUp do, via TrackingScripts — so whether the send reaches Ads from here depends
// on the Google tag's linked destinations, which is external configuration this repo cannot prove
// either way. GTM-KMQZ7CSX also loads on every page and may carry its own conversion tags;
// inspect the live container before drawing conclusions about the Ads count.
const ADS_PURCHASE_CONVERSION_ID = "AW-11423224913/qQQwCIzjuPoYENGogscq";

// A package_detail row's plan code. `code` FIRST: that row's `package_type` is the package GROUP
// (services/users.py), so preferring it never matches a plan code.
const codeOf = (row: any) => (row?.code || row?.package_type || "").toLowerCase();

/**
 * Label and value for the report. The subscription row for the checkout's price is the truth:
 * its `code` is the plan bought, its `total_price` the estimated value (catalog × seats). Without that
 * row (no pending price — a new tab, an expired value — or a webhook slower than the retry window)
 * the label falls back to the legacy read-back code, exactly as before this change. The value then
 * comes from the row for that code only when there was no pending price; with one, no other row can
 * be the order, and the documented 1.0 placeholder is the honest answer.
 */
export const resolvePurchase = (purchased: PurchasedPackage, priceId: string | undefined): { packageType: string; purchaseValue: number | undefined } => {
  const row = findPurchasedRow(purchased.packageDetail, priceId);

  if (row) return { packageType: codeOf(row), purchaseValue: typeof row.total_price === "number" ? row.total_price : undefined };

  const packageType = purchased.packageType;
  const fallbackRow = priceId ? undefined : purchased.packageDetail.find((pkg) => codeOf(pkg) === packageType);

  return { packageType, purchaseValue: typeof fallbackRow?.total_price === "number" ? fallbackRow.total_price : undefined };
};

// Fire-and-forget platforms: one throwing must not stop the others, and must not propagate — by
// the time any of them runs the order's fired marker is already written.
const safely = (send: () => void) => {
  try {
    send();
  } catch {
    // no-op
  }
};

interface EmitPurchaseArgs {
  orderId: string;
  userEmail: string;
  packageType: string;
  purchaseValue: number | undefined;
}

const emitPurchase = ({ orderId, userEmail, packageType, purchaseValue }: EmitPurchaseArgs) => {
  // Sends the estimated subscription value for value-based bidding (falls back to 1.0 if the lookup
  // misses, so a conversion is still recorded). Enhanced Conversions: gtag SHA-256-hashes the
  // email client-side. `trackConversion` is a no-op off app.refabric.com — see utils/analytics.
  safely(() => trackConversion(ADS_PURCHASE_CONVERSION_ID, { value: purchaseValue ?? 1.0, currency: "USD", transaction_id: orderId, user_data: { email: userEmail } }));

  // GA4 purchase, keyed to the plan. `action: "purchase"` is carried over from the deprecated
  // AnalyticsEvent() shape; its empty `category` is dropped per docs/rules/analytics-events.md.
  const purchaseParams = purchaseValue != null ? { value: purchaseValue, currency: "USD" } : {};
  const purchaseEvent = PURCHASE_EVENT_BY_PACKAGE_TYPE[packageType];

  safely(() => {
    if (purchaseEvent) trackEvent(purchaseEvent, { action: "purchase", ...purchaseParams });
    else trackEvent(EVENTS.PRICING.PURCHASE_OTHER, { action: "purchase", ...purchaseParams, package_type: packageType || "unknown" });
  });

  // Meta Purchase. Same value/currency as GA4 (0 fallback so the standard event still fires).
  safely(() => trackFacebookEvent("Purchase", { value: purchaseValue ?? 0, currency: "USD" }));
};

/* ------------------------------------------------------------------------------------------
 * Success-page orchestration
 * ---------------------------------------------------------------------------------------- */

const firedKey = (orderId: string) => `purchase_fired_${orderId}`;

const storageGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // no-op — the in-memory guard still covers this document
  }
};

export type SuccessPageReportOutcome = "deduped" | "reported" | "not_reported";

interface ReportOnSuccessPageArgs {
  orderId: string;
  userId: string;
  userEmail: string;
  /** Per-mount set of order ids this document has claimed — the caller's `useRef(new Set())`. */
  inFlight: Set<string>;
  now?: number;
}

/**
 * Report one logged-in purchase from `/payment/success/:orderId`, at most once per order.
 *
 * Two guards, each doing one job:
 *  - `inFlight` (memory, keyed by ORDER, taken synchronously before the first await) stops the
 *    2-4 re-runs this effect gets per load — React.StrictMode, Router's `setCheckingToken(false)`,
 *    `useUserDetailRefresh`'s `setUser` — from each starting a read and each emitting. It dies
 *    with the document, which is exactly right: a reload mid-request destroys the continuation.
 *  - the persisted `purchase_fired_<orderId>` marker is checked and written in the SAME
 *    synchronous stretch as the emission, after the read. So it is never held by a request that
 *    can be abandoned — writing it before the await made a reload during the 0-5 s read lose the
 *    conversion for good, since nothing would ever release it — and it is always written before
 *    anything is sent, so no path can emit twice or release it after a partial emission.
 *
 * Evidence rule: nothing is emitted without a successful `getUserDetail`. On its own that proves a
 * signed-in session reading its own profile — NOT that a payment succeeded; the success route is a
 * URL anyone signed in can type. When the checkout's subscription row is found it additionally
 * proves an active/trialing/past_due subscription at that price exists for the team. A failed
 * read is never traded for an unverified conversion.
 */
export const reportPurchaseOnSuccessPage = async ({ orderId, userId, userEmail, inFlight, now = Date.now() }: ReportOnSuccessPageArgs): Promise<SuccessPageReportOutcome> => {
  if (inFlight.has(orderId) || storageGet(firedKey(orderId))) return "deduped";

  inFlight.add(orderId);

  const pending = readPendingPurchase(now);

  let purchased: PurchasedPackage | null = null;

  try {
    purchased = await readPurchasedPackage(userId, { priceId: pending?.priceId });
  } catch {
    purchased = null;
  }

  if (!purchased) {
    // Nothing sent, nothing persisted: let a later run in this document, or a reload, retry. The
    // pending value is left in place so that retry can still label correctly.
    inFlight.delete(orderId);

    return "not_reported";
  }

  // ---- synchronous from here: check → mark → emit, with no await in between ----
  if (storageGet(firedKey(orderId))) return "deduped";

  storageSet(firedKey(orderId), "1");

  emitPurchase({ orderId, userEmail, ...resolvePurchase(purchased, pending?.priceId) });

  // Consumed. Cleared only if it is still the value we read, so a checkout started while this
  // report was in flight keeps its own attribution.
  if (pending) clearPendingPurchasePackage(pending.createdAt);

  return "reported";
};
